import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, Navigate } from 'react-router-dom';
import { Star, MapPin, ArrowRight, Search, Sparkles } from 'lucide-react';
import { api } from '../App';
import Header from '../components/Header';
import { Footer } from '../components/landing/Footer';
import { SEO } from '../components/SEO';

// Programmatic SEO landing page, `/{citySlug}/{serviceSlug}`.
//
// Validates both slugs against the seo_cities + seo_services
// allow-list before rendering. Unknown 2-segment URLs (any path that
// isn't on the allow-list) get the 404 path so this route can't shadow
// future top-level routes. Zero-provider matches do NOT 404, they show
// the "be first" provider CTA so the page still earns SEO equity and
// converts inbound provider signups.
export default function CityServicePage() {
  const { citySlug, serviceSlug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | not-found

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    api
      .get(`/seo/directory/page?city=${encodeURIComponent(citySlug || '')}&service=${encodeURIComponent(serviceSlug || '')}`)
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        setStatus('ok');
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setStatus('not-found');
        } else {
          // Network or 5xx, treat the page as missing so we don't show a
          // broken layout. CatchAllRoute handles brand-consistent 404.
          setStatus('not-found');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [citySlug, serviceSlug]);

  if (status === 'not-found') {
    // Unknown slug, fall through to the home redirect, matching the
    // behaviour of App.js's CatchAllRoute for any non-existent path.
    return <Navigate to="/" replace />;
  }

  if (status === 'loading' || !data) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20">
          <div className="h-10 w-2/3 bg-stone-200 rounded animate-pulse mb-4" />
          <div className="h-5 w-1/2 bg-stone-100 rounded animate-pulse mb-12" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={`city-service-skeleton-${i}`} className="bg-stone-100 h-72 rounded-xl animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const { city, service, providers, meta, schema_jsonld, page_url } = data;
  const hasProviders = providers.length > 0;

  // Funnel attribution: when the empty-state CTA is clicked, prefill
  // the city + service on the provider register flow so onboarding is
  // friction-free for the first stylist claiming the city.
  const claimCityHref =
    `/register?role=provider&category=${service.category_id}&from=seo_${city.slug}_${service.slug}&city=${encodeURIComponent(city.name)}`;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <SEO
        title={meta.title}
        description={meta.description}
        url={page_url}
        jsonLd={schema_jsonld}
      />
      <Header />

      {/* Hero */}
      <section className="bg-white border-b border-stone-100">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-16">
          <nav className="text-sm text-stone-500 mb-4" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#1A1A1A]">Zuriya</Link>
            <span className="mx-2">/</span>
            <Link to="/discover" className="hover:text-[#1A1A1A]">Discover</Link>
            <span className="mx-2">/</span>
            <span className="text-[#1A1A1A]">{city.name}</span>
            <span className="mx-2">/</span>
            <span className="text-[#1A1A1A]">{service.name_plural}</span>
          </nav>
          <h1
            className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-[#1A1A1A] mb-3"
            data-testid="city-service-h1"
          >
            {meta.h1}
          </h1>
          <p className="text-stone-500 text-base md:text-lg max-w-2xl mb-6">
            {meta.subhead}
          </p>
          <p className="text-stone-600 text-base max-w-3xl leading-relaxed">
            {meta.intro}
          </p>
        </div>
      </section>

      {/* Provider grid OR empty-state */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          {hasProviders ? (
            <>
              <div className="flex items-baseline justify-between mb-8">
                <h2 className="font-heading text-2xl font-medium text-[#1A1A1A]">
                  {providers.length} {providers.length === 1 ? 'stylist' : 'stylists'} taking bookings
                </h2>
                <Link
                  to={`/discover?category=${service.category_id}`}
                  className="text-sm text-[#1A1A1A] hover:text-[#D4AF37] underline underline-offset-4 decoration-[#D4AF37]/40"
                  data-testid="city-service-browse-all"
                >
                  Browse all {service.name_plural.toLowerCase()}
                </Link>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="city-service-grid">
                {providers.map((p) => {
                  const href = p.username ? `/@${p.username}` : `/provider/${p.provider_id}`;
                  return (
                    <Link
                      key={p.provider_id}
                      to={href}
                      className="card-surface p-5 hover:shadow-lg transition-shadow group"
                      data-testid={`city-service-provider-${p.provider_id}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-16 h-16 rounded-full bg-stone-100 overflow-hidden flex-shrink-0">
                          {p.profile_image ? (
                            <img src={p.profile_image} alt={p.brand_name || 'Stylist'} className="w-full h-full object-cover" loading="lazy" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-stone-400 font-medium">
                              {(p.brand_name || '?').slice(0, 1)}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-[#1A1A1A] truncate group-hover:text-[#D4AF37] transition-colors">
                            {p.brand_name}
                          </h3>
                          {p.location && (
                            <p className="text-xs text-stone-500 flex items-center gap-1 mt-1">
                              <MapPin className="h-3 w-3" />
                              <span className="truncate">{p.location}</span>
                              <span className="text-stone-400">,</span>
                              <span>{p.distance_miles} mi</span>
                            </p>
                          )}
                          {(p.rating || 0) > 0 && (
                            <p className="text-xs text-stone-600 flex items-center gap-1 mt-1">
                              <Star className="h-3 w-3 text-[#D4AF37] fill-[#D4AF37]" />
                              <span className="font-medium">{(p.rating || 0).toFixed(1)}</span>
                              <span className="text-stone-400">({p.review_count || 0})</span>
                            </p>
                          )}
                        </div>
                      </div>
                      {p.bio && (
                        <p className="text-sm text-stone-500 mt-3 line-clamp-2">{p.bio}</p>
                      )}
                    </Link>
                  );
                })}
              </div>
            </>
          ) : (
            <div
              className="card-surface p-8 md:p-12 text-center max-w-2xl mx-auto"
              data-testid="city-service-empty"
            >
              <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="h-7 w-7 text-[#D4AF37]" />
              </div>
              <h2 className="font-heading text-2xl font-medium text-[#1A1A1A] mb-3">
                {meta.empty_title}
              </h2>
              <p className="text-stone-600 mb-6 max-w-md mx-auto">
                {meta.empty_body}
              </p>
              <Link
                to={claimCityHref}
                className="inline-flex items-center gap-2 btn-primary h-12 px-6 rounded-full text-sm"
                data-testid="city-service-claim-cta"
              >
                Claim {city.name}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <p className="text-xs text-stone-400 mt-4">
                Free to list, no commitment, set up in 10 minutes.
              </p>
            </div>
          )}

          {/* Sibling services in this city, helps SEO crawler discover related pages */}
          <div className="mt-16 pt-12 border-t border-stone-100">
            <h3 className="text-sm font-semibold text-stone-500 tracking-wider uppercase mb-4">
              More in {city.name}
            </h3>
            <div className="flex flex-wrap gap-2" data-testid="city-service-siblings">
              {[
                { slug: 'braids', label: 'Braids' },
                { slug: 'hair-care', label: 'Hair Care' },
                { slug: 'barbers', label: 'Barbers' },
                { slug: 'aesthetics', label: 'Aesthetics' },
                { slug: 'nails', label: 'Nails' },
                { slug: 'lashes-brows', label: 'Lashes & Brows' },
                { slug: 'makeup', label: 'Make-up' },
              ]
                .filter((s) => s.slug !== service.slug)
                .map((s) => (
                  <Link
                    key={s.slug}
                    to={`/${city.slug}/${s.slug}`}
                    className="px-4 py-2 rounded-full bg-white border border-stone-200 text-sm text-stone-700 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                    data-testid={`city-service-sibling-${s.slug}`}
                  >
                    {s.label} in {city.name}
                  </Link>
                ))}
            </div>
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/discover')}
              className="text-sm text-stone-500 hover:text-[#1A1A1A] inline-flex items-center gap-2"
            >
              <Search className="h-3.5 w-3.5" />
              Search all of the UK
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
